import { KeyboardArrowDown, KeyboardArrowLeft } from "@mui/icons-material";
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import cx from "classnames";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as CrownIcon } from "assets/crown.svg";
import BillingTab from "components/Payment/UpgradeFlowComponents/BillingTab";
import UsageTab from "components/Payment/UpgradeFlowComponents/UsageTab";
import {
  SLAPSHOT_HOME_URL,
  SLAPSHOT_MY_PLAN_URL,
  SLAPSHOT_SUBSCRIPTION_URL,
} from "constants/urls";
import { UserContext } from "layouts/SlapshotMainLayout";
import { Invoice, InvoiceCompact } from "models/Payment";
import { Subscription } from "models/User";
import {
  CANCEL_PLAN_INDEX,
  PLANS_INDEX,
} from "pages/UpgradePlanFlow/UpgradePlanFlow";
import { exportUsage } from "services/SubscriptionService";

import styles from "./MyPlan.module.scss";

interface MyPlanProps {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  subscription: Subscription;
}

const TAB_BILLING = 0;
const TAB_USAGE = 1;

enum exportFormats {
  PDF = "PDF",
  CSV = "CSV",
}

enum exportDurations {
  _6M = "_6M",
  _1Y = "_1Y",
  _2Y = "_2Y",
}

const MyPlan: React.FC<MyPlanProps> = ({ setStep, subscription }) => {
  const [tab, setTab] = useState(TAB_BILLING);

  const [exportFormat, setExportFormat] = useState(exportFormats.PDF);
  const [exportDuration, setExportDuration] = useState(exportDurations._1Y);

  const navigate = useNavigate();
  const location = useLocation();

  const handleExport = useCallback(async () => {
    try {
      const res = await exportUsage(
        subscription?.id,
        exportFormat,
        exportDuration,
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;

      const filename = `Usage Report.${exportFormat.toLowerCase()}`;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting usage:", error);
    }
  }, [exportDuration, exportFormat, subscription]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    if (tabParam === "usage") {
      setTab(TAB_USAGE);
      searchParams.delete("tab");
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate]);

  return (
    <div className={styles.container}>
      <div
        className={styles.back}
        onClick={() => {
          navigate(-1);
          setTimeout(() => {
            const path = window.location.pathname;
            if (
              path.startsWith(SLAPSHOT_SUBSCRIPTION_URL) ||
              path.startsWith(SLAPSHOT_MY_PLAN_URL)
            ) {
              navigate(SLAPSHOT_HOME_URL);
            }
          }, 100);
        }}
      >
        <KeyboardArrowLeft className={styles.arrowLeft} />
        <span>Back</span>
      </div>

      <div className={styles.header}>
        <div className={styles.tabs}>
          <div
            className={cx(
              styles.tab,
              tab === TAB_BILLING && styles.tabSelected,
            )}
            onClick={() => {
              setTab(TAB_BILLING);
            }}
          >
            Billing
          </div>

          <div
            onClick={() => {
              setTab(TAB_USAGE);
            }}
            className={cx(styles.tab, tab === TAB_USAGE && styles.tabSelected)}
          >
            Usage
          </div>
        </div>

        {tab === TAB_BILLING &&
          subscription &&
          subscription.plan.price.unit_amount_decimal > 0 &&
          subscription.renewal_plan.price.unit_amount_decimal > 0 && (
            <Button
              disabled={subscription.is_processing_payment}
              onClick={() => setStep(CANCEL_PLAN_INDEX)}
              className={cx(styles.outlineButtonRed, styles.cancelPlanBtn)}
            >
              Cancel Plan
            </Button>
          )}

        {tab === TAB_USAGE && (
          <div className={styles.tabActions}>
            {subscription?.usage &&
              !!Object.keys(subscription.usage).length && (
                <Tooltip
                  arrow
                  disableFocusListener
                  placement="bottom-end"
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "transparent",
                        "& .MuiTooltip-arrow": {
                          color: "#22242a",
                          fontSize: "large",
                          top: "4px !important",
                        },
                      },
                    },
                  }}
                  title={
                    <div className={styles.expCon}>
                      <div>
                        <div className={styles.radioGrpLabel}>Duration:</div>
                        <RadioGroup
                          value={exportDuration}
                          onChange={(e) => {
                            setExportDuration(
                              e.target.value as exportDurations,
                            );
                          }}
                          sx={{ color: "#CCCCDC" }}
                        >
                          <FormControlLabel
                            value={exportDurations._6M}
                            control={
                              <Radio className={styles.radioBtn} size="small" />
                            }
                            label="Last 6 months"
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "14px",
                                fontFamily: "Inter",
                              },
                            }}
                          />
                          <FormControlLabel
                            value={exportDurations._1Y}
                            control={
                              <Radio className={styles.radioBtn} size="small" />
                            }
                            label="1 Year"
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "14px",
                                fontFamily: "Inter",
                              },
                            }}
                          />
                          <FormControlLabel
                            value={exportDurations._2Y}
                            control={
                              <Radio className={styles.radioBtn} size="small" />
                            }
                            label="2 Years"
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "14px",
                                fontFamily: "Inter",
                              },
                            }}
                          />
                        </RadioGroup>
                      </div>
                      <div>
                        <div className={styles.radioGrpLabel}>Format:</div>
                        <RadioGroup
                          value={exportFormat}
                          onChange={(e) => {
                            setExportFormat(e.target.value as exportFormats);
                          }}
                          sx={{ color: "#CCCCDC" }}
                        >
                          <FormControlLabel
                            value={exportFormats.CSV}
                            control={
                              <Radio className={styles.radioBtn} size="small" />
                            }
                            label="As a CSV"
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "14px",
                                fontFamily: "Inter",
                              },
                            }}
                          />
                          <FormControlLabel
                            value={exportFormats.PDF}
                            control={
                              <Radio className={styles.radioBtn} size="small" />
                            }
                            label="As a PDF"
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "14px",
                                fontFamily: "Inter",
                              },
                            }}
                          />
                        </RadioGroup>
                      </div>
                    </div>
                  }
                >
                  <Button
                    className={cx(styles.outlineButton, styles.exportBtn)}
                    onClick={handleExport}
                  >
                    Export as <KeyboardArrowDown />
                  </Button>
                </Tooltip>
              )}

            <div className={styles.upgradeBtncon}>
              <Button
                className={styles.upgrade}
                onClick={() => setStep(PLANS_INDEX)}
              >
                <CrownIcon className={styles.crownIcon} />
                Upgrade
              </Button>
            </div>
          </div>
        )}
      </div>

      {tab === TAB_BILLING && (
        <BillingTab setStep={setStep} subscription={subscription} />
      )}

      {tab === TAB_USAGE && <UsageTab subscription={subscription} />}
    </div>
  );
};

export default MyPlan;
